import {
  Box,
  HStack,
  Icon,
  ModalBody,
  ModalContent,
  ModalFooter,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import hj from "@hotjar/browser";
import { type TezosToolkit } from "@taquito/taquito";
import {
  ChainFullIds,
  type ImplicitAccount,
  type SafeSendAssetType,
  type Transaction,
  getHash,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { runWithdraw } from "@umami/safesend";
import { useCurrentAccount, useSelectedNetwork } from "@umami/state";
import { FormProvider, useForm } from "react-hook-form";

import { useColor } from "../../../styles/useColor";
import { FormPageHeader } from "../FormPageHeader";
import { useSignPageHelpers } from "../utils";

import { biInfoIcon } from "@/assets/icons";
import { SignButton } from "@/components/SendFlow/SignButton";
import { SuccessStep } from "@/components/SendFlow/SuccessStep";

type FormValues = {
  assetType: SafeSendAssetType;
  secretHash: string;
};

export const SignPage = ({ operation }: { operation: Transaction }) => {
  const color = useColor();
  const sender = useCurrentAccount();
  const network = useSelectedNetwork();
  const { openWith } = useDynamicModalContext();
  const form = useForm<FormValues>({
    defaultValues: {
      assetType: operation.assetType,
      secretHash: operation.parameter.value.hash_,
    },
  });

  const referenceValue = operation.parameter.value.comment;

  const { isLoading, signer } = useSignPageHelpers(
    {
      type: "implicit",
      sender: sender as ImplicitAccount,
      signer: sender as ImplicitAccount,
      operations: [
        {
          type: "withdraw_safe_send",
          assetType: operation.assetType,
          secretHash: operation.parameter.value.hash_,
          contract: {
            type: "contract",
            pkh: "initialValue",
          },
          entrypoint: "withdraw",
          amount: "0",
          args: [],
        },
      ],
      estimates: [],
    },
    ChainFullIds.TEZOS
  );

  const onSign = async (tezosToolkit: TezosToolkit) => {
    const batchOperation = await runWithdraw(
      tezosToolkit,
      network,
      form.getValues("secretHash"),
      form.getValues("assetType")
    );
    await openWith(<SuccessStep hash={getHash(batchOperation)} />, { canBeOverridden: true });

    return batchOperation;
  };

  hj.stateChange("safe_send/withdraw_form_page");

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form>
          <FormPageHeader title="Withdraw Transaction" />
          <ModalBody>
            <Text color={color("700")} fontSize="14px" textAlign="center">
              Are you sure you want to Withdraw the transaction?
            </Text>
            {referenceValue && (
              <VStack align="stretch" width="full" marginTop="30px" spacing="10px">
                <HStack>
                  <Text fontSize="18px" fontWeight="semibold">
                    Reference
                  </Text>
                  <Tooltip label="Comment added to transaction">
                    <Icon as={biInfoIcon} boxSize="24px" color={color("400")} cursor="help" />
                  </Tooltip>
                </HStack>
                <Box padding={4} background={color("100")} borderRadius="md">
                  <Text fontSize="18px">{referenceValue}</Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter marginTop="0">
            <SignButton
              isLoading={isLoading}
              network={network}
              onSubmit={onSign}
              signer={signer}
              text="Confirm"
            />
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};
